import WarehouseOutlined from "@mui/icons-material/WarehouseOutlined";
import { Typography, useTheme } from "@mui/material";
import { Grid } from "@mui/system";
import { DisplayFieldWithTooltip } from "components/DisplayFieldWithTooltip";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { formatAddress } from "encamp-shared/src/utils/address";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { WasteManifestSource } from "generated-graphql/graphql";
import { getDaysInTransitInfo } from "util/manifest";

import { FieldSourceValues } from "../useManifestDiscrepancyFields";
import { ManifestPartyCard } from "./ManifestPartyCard";
import { SourcedField } from "./SourcedField";
import { ManifestDetail } from "./types";

interface ReceivingTsdfSectionProps {
  manifest: ManifestDetail;
  source: WasteManifestSource;
  availableSources: readonly WasteManifestSource[];
  valuesByField: Map<string, FieldSourceValues>;
  /** UI field keys with an open cross-source discrepancy — drives warning icons. */
  disagreementFields: Set<string>;
  /** Opens the full compare dialog from a field's provenance popover (staff only). */
  onOpenCompare?: () => void;
}

/**
 * Receiving (TSDF) card: destination name/EPA/address vary by source (vendor vs
 * e-Manifest); the received date and days-in-transit come from the e-Manifest.
 */
export function ReceivingTsdfSection({
  manifest,
  source,
  availableSources,
  valuesByField,
  disagreementFields,
  onOpenCompare,
}: ReceivingTsdfSectionProps) {
  const theme = useTheme();
  const daysInTransitFields = getDaysInTransitInfo(
    manifest.eManifestDaysInTransit,
    manifest.receivedDate,
    theme
  );

  return (
    <ManifestPartyCard title="Receiving (TSDF)" icon={<WarehouseOutlined fontSize="small" />}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SourcedField
            label="TSDF"
            source={source}
            availableSources={availableSources}
            values={{
              [WasteManifestSource.EManifest]: manifest.desFacilityName,
              [WasteManifestSource.Vendor]: manifest.vendorManifest?.desFacilityName,
            }}
            discrepancyValues={valuesByField.get("destinationFacilityName")}
            hasDisagreement={disagreementFields.has("destinationFacilityName")}
            onOpenCompare={onOpenCompare}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <SourcedField
            label="TSDF EPA ID"
            source={source}
            availableSources={availableSources}
            values={{
              [WasteManifestSource.EManifest]: manifest.desFacilityId,
              [WasteManifestSource.Vendor]: manifest.vendorManifest?.desFacilityId,
            }}
            discrepancyValues={valuesByField.get("destinationFacilityId")}
            hasDisagreement={disagreementFields.has("destinationFacilityId")}
            onOpenCompare={onOpenCompare}
          />
        </Grid>
        <Grid size={12}>
          <SourcedField
            label="TSDF Address"
            source={source}
            availableSources={availableSources}
            values={{
              [WasteManifestSource.EManifest]: formatAddress(
                manifest.desFacLocationStreet1,
                manifest.desFacLocationCity,
                manifest.desFacLocationState,
                manifest.desFacLocationZip
              ),
              [WasteManifestSource.Vendor]: formatAddress(
                manifest.vendorManifest?.desFacLocationStreet1,
                manifest.vendorManifest?.desFacLocationCity,
                manifest.vendorManifest?.desFacLocationState,
                manifest.vendorManifest?.desFacLocationZip
              ),
            }}
            onOpenCompare={onOpenCompare}
          />
        </Grid>
        {manifest.isRegulatedEManifest && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <DisplayOnlyField
              label="Received by TSDF"
              copyable={false}
              text={manifest.receivedDate ? getUtcDisplayDate(manifest.receivedDate) : undefined}
              renderValue={(value) =>
                value ? (
                  value
                ) : (
                  <Typography fontStyle="italic" color="text.secondary" variant="body2">
                    Pending
                  </Typography>
                )
              }
            />
          </Grid>
        )}
        {daysInTransitFields && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <DisplayFieldWithTooltip
              text={daysInTransitFields.display}
              textColor={daysInTransitFields.color}
              isItalic={!manifest.receivedDate}
              label="Days In Transit"
              tooltip={daysInTransitFields.tooltipText}
              tooltipEnterDelay={500}
              tooltipPlacement="right"
              transparentBackground
            />
          </Grid>
        )}
      </Grid>
    </ManifestPartyCard>
  );
}
